import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { describeFailure } from '@/lib/describe-failure';
import { getQualityMetricConfiguration } from '@/api/quality-metric.api.private';
import { FEATURES } from 'shared/constants/features.constants';
import { QUALITY_METRIC_ERROR_CODES } from 'shared/constants/quality-metric.constants';
import type { QualityMetricOrganizationConfiguration } from 'shared/api/quality-metric-rq.service.api';
import { useOrganizationContext } from '../../organization.context';
import { SOURCE, mapperLabel } from './constants';
import IngestionSheet from './ingestion-sheet';

/**
 * Summary of how this organization's quality metrics are ingested, with the
 * configuration itself behind a button.
 *
 * The card answers "is this organization pulling, and is it healthy?" and
 * nothing else; everything that changes ingestion — the parser, the MPOG
 * connection, the scheduled runs — lives in one panel, so there is a single
 * place to go and no second route to the same setting.
 *
 * @returns The summary card, or `null` for an organization without the
 *   `QUALITY_METRICS` feature. Takes the organization from
 *   `useOrganizationContext`, so it has no props.
 * @example
 * <QualityMetricsCard />
 */
export default function QualityMetricsCard() {
  const { organization } = useOrganizationContext();
  const hasFeature = !!organization.features?.includes(FEATURES.QUALITY_METRICS);

  const [configuration, setConfiguration] = useState<QualityMetricOrganizationConfiguration | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  /**
   * Runs are written through their own endpoints, which never hand a fresh
   * configuration document back — so the run count and the "nothing is
   * scheduled" warning here would keep reading the document loaded on mount.
   * Re-read on close rather than threading the sheet's list back up: the sheet
   * is the only thing that can have changed it, and it has just gone away.
   */
  const onSheetOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) load(() => false, { quiet: true });
  };

  const load = useCallback(
    async (isCancelled: () => boolean = () => false, { quiet = false } = {}) => {
      // A quiet reload keeps the summary on screen while it refreshes. The
      // first read has nothing to show yet, so only that one blanks the card.
      if (!quiet) setLoading(true);
      setLoadError(null);

      const res = await getQualityMetricConfiguration();
      if (isCancelled()) return;

      // No document yet is the normal starting state for an organization nobody
      // has configured, not a failure: show the empty summary.
      if (res.error && res.code !== QUALITY_METRIC_ERROR_CODES.CONFIGURATION_NOT_FOUND) {
        // Opening the panel off a failed read would make every confirmation
        // quote the wrong "current" value, so it stays shut until a read lands.
        setLoadError(describeFailure(res));
        setLoading(false);
        return;
      }

      setConfiguration(res.error ? null : res.data);
      setLoading(false);
    },
    [],
  );

  // The routes read the session organization. This card renders only after the
  // organization page has switched the session into `organization` (the page
  // shows a loader until then), and the id is a dependency so another
  // organization always re-reads rather than keeping this one's document.
  useEffect(() => {
    if (!hasFeature) return undefined;
    let cancelled = false;
    load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [hasFeature, load, organization.id]);

  if (!hasFeature) return null;

  const defaultMapper = configuration?.mapper?.default ?? null;
  const { configurationName = '', secretName, runs = [] } = configuration?.[SOURCE] ?? {};
  const isPullEnabled = !!configurationName && !!secretName;
  const enabledRuns = runs.filter(run => run.enabled);

  // Scheduled and pull-enabled are independent, and either one alone ingests
  // nothing — so the card says which half is missing rather than one verdict.
  const warning = !isPullEnabled
    ? 'Not pull-enabled: the MPOG site name and credentials are both needed before the sync service will pull this organization.'
    : enabledRuns.length === 0
      ? 'Pull-enabled, but nothing is scheduled — no data will arrive until a run is added.'
      : !defaultMapper
        ? 'No default parser: any pull-enabled metric that does not set its own is skipped.'
        : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Quality metrics ingestion</CardTitle>
        <CardDescription>
          Which parser this organization&apos;s exports are read with, the MPOG site they come from,
          and when that site is pulled.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && <p className="text-muted-foreground text-sm">Loading the configuration...</p>}

        {!loading && loadError && (
          <div className="space-y-4">
            <p className="text-destructive text-sm">{loadError}</p>
            <Button variant="outline" onClick={() => load()}>
              Retry
            </Button>
          </div>
        )}

        {!loading && !loadError && (
          <>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant={defaultMapper ? 'secondary' : 'destructive'}>
                {defaultMapper ? mapperLabel(defaultMapper) : 'No default parser'}
              </Badge>
              <Badge variant={isPullEnabled ? 'default' : 'destructive'}>
                {isPullEnabled ? 'Pull-enabled' : 'Not pull-enabled'}
              </Badge>
            </div>

            <p className="text-sm">
              {runs.length === 0
                ? 'No scheduled runs — nothing is pulled automatically.'
                : `${runs.length} scheduled run${runs.length === 1 ? '' : 's'} · ${enabledRuns.length} enabled`}
            </p>

            {warning && (
              <p className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-700">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{warning}</span>
              </p>
            )}

            <Button onClick={() => onSheetOpenChange(true)}>Manage ingestion</Button>
          </>
        )}
      </CardContent>

      {/* Mounted only while open, so the forms re-seed from the stored document
          on every open and no typed secret survives a close. */}
      {open && (
        <IngestionSheet
          open={open}
          onOpenChange={onSheetOpenChange}
          configuration={configuration}
          onConfigurationChange={setConfiguration}
        />
      )}
    </Card>
  );
}
